import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { proposalAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatINR, formatPercent, formatDate, getStatusBadgeClass, shortId, getMarginBadgeClass, apiError } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  FileText, 
  Download,
  Eye,
  Edit2,
  Trash2,
  Loader2,
  Filter
} from 'lucide-react';

const STATUSES = ['draft', 'sent', 'accepted', 'rejected', 'in_progress', 'completed'];

export default function Proposals() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchProposals();
  }, []);

  const fetchProposals = async () => {
    try {
      const response = await proposalAPI.getAll();
      setProposals(response.data);
    } catch (error) {
      toast.error(apiError(error, 'Failed to fetch proposals'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (proposal) => {
    setDownloading(proposal.id);
    try {
      const response = await proposalAPI.downloadPdf(proposal.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `proposal_${shortId(proposal.id)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error(apiError(error, 'Failed to download PDF'));
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this proposal?')) return;
    
    try {
      await proposalAPI.delete(id);
      toast.success('Proposal deleted');
      fetchProposals();
    } catch (error) {
      toast.error(apiError(error, 'Failed to delete proposal'));
    }
  };

  const filteredProposals = proposals.filter(proposal => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = (proposal.project_name || '').toLowerCase().includes(q) ||
                         (proposal.client_name || '').toLowerCase().includes(q);
    const matchesStatus = filterStatus === 'all' || proposal.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Stats
  const totalValue = proposals.reduce((sum, p) => sum + (p.final_price || 0), 0);
  const acceptedValue = proposals.filter(p => ['accepted', 'completed'].includes(p.status)).reduce((sum, p) => sum + (p.final_price || 0), 0);
  const avgMargin = proposals.length > 0 
    ? proposals.reduce((sum, p) => sum + (p.margin_percentage || 0), 0) / proposals.length 
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Proposals</h1>
          <p className="text-gray-500 mt-1">Create and manage client proposals</p>
        </div>
        {can('write') && (
          <Link to="/proposals/new">
            <Button data-testid="new-proposal-btn" className="bg-black hover:bg-black/90">
              <Plus className="w-4 h-4 mr-2" />
              New Proposal
            </Button>
          </Link>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-500">Total Proposals</p>
            <p className="text-2xl font-bold font-mono mt-1">{proposals.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-500">Pipeline Value</p>
            <p className="text-2xl font-bold font-mono mt-1">{formatINR(totalValue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-500">Won Value</p>
            <p className="text-2xl font-bold font-mono mt-1 text-green-700">{formatINR(acceptedValue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-500">Avg. Margin</p>
            <p className={`text-2xl font-bold font-mono mt-1 ${avgMargin >= 30 ? 'text-green-700' : avgMargin >= 20 ? 'text-orange-600' : 'text-red-700'}`}>
              {formatPercent(avgMargin)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            data-testid="proposal-search"
            placeholder="Search by project or client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {STATUSES.map(status => (
              <SelectItem key={status} value={status} className="capitalize">{status}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Proposals Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th className="table-header text-left">ID</th>
                  <th className="table-header text-left">Project</th>
                  <th className="table-header text-left">Client</th>
                  <th className="table-header text-right">Value</th>
                  <th className="table-header text-center">Margin</th>
                  <th className="table-header text-center">Status</th>
                  <th className="table-header text-left">Due Date</th>
                  <th className="table-header text-center">Ver.</th>
                  <th className="table-header text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProposals.length > 0 ? (
                  filteredProposals.map((proposal) => (
                    <tr key={proposal.id} className="table-row" data-testid={`proposal-row-${proposal.id}`}>
                      <td className="table-cell font-mono text-xs text-gray-500">{shortId(proposal.id)}</td>
                      <td className="table-cell font-medium">
                        <Link to={`/proposals/${proposal.id}`} className="hover:underline">
                          {proposal.project_name}
                        </Link>
                      </td>
                      <td className="table-cell text-gray-600">{proposal.client_name}</td>
                      <td className="table-cell text-right font-mono">{formatINR(proposal.final_price)}</td>
                      <td className="table-cell text-center">
                        <span className={`badge ${getMarginBadgeClass(proposal.margin_percentage)}`}>
                          {formatPercent(proposal.margin_percentage)}
                        </span>
                      </td>
                      <td className="table-cell text-center">
                        <span className={`badge ${getStatusBadgeClass(proposal.status)}`}>
                          {proposal.status}
                        </span>
                      </td>
                      <td className="table-cell text-gray-600">{formatDate(proposal.due_date)}</td>
                      <td className="table-cell text-center">
                        <span className="text-xs text-gray-500">v{proposal.version || 1}</span>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/proposals/${proposal.id}`)}
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {can('write') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/proposals/${proposal.id}/edit`)}
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDownloadPdf(proposal)}
                            disabled={downloading === proposal.id}
                            title="Download PDF"
                          >
                            {downloading === proposal.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </Button>
                          {can('delete') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(proposal.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-12 text-gray-400">
                      <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No proposals found</p>
                      {can('write') && proposals.length === 0 && (
                        <Link to="/proposals/new">
                          <Button variant="outline" size="sm" className="mt-4">
                            Create your first proposal
                          </Button>
                        </Link>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
